import { AppConfigContext } from '@App/AppConfigContext';
import providerData from '@App/data/provider.zava.json';
import { ensureHash } from '@App/helper/ensureHash';
import { useVideoConsultationStore } from '@App/stores';
import { Features } from '@Config/Features';
import { Area } from '@Domain/model/area';
import { InsuranceType } from '@Framework/constants/InsuranceType';
import { ZAVA_VIDEO_CONSULTATION_REDIRECT_URI } from '@Framework/constants/ProviderUrls';
import { useInsuranceTypeParam } from '@Framework/hooks/useInsuranceTypeParam';
import { Box, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';

import { Button } from '../../../atoms/Button/Button';
import Feature from '../../../atoms/Feature';
import { OdsLayout } from '../../../layouts/odsLayout';
import { BenefitBox } from '../../../molecules/BenefitBox/BenefitBox';
import { TimeslotsList } from '../../../molecules/TimeslotsList/TimeslotsList';
import { ListBox } from '../../../organisms/ListBox/ListBox';
import { NotificationAppModal } from '../../../organisms/NotificationAppModal/NotificationAppModal';
import { ProviderTile } from '../../../organisms/ProviderTile/ProviderTile';
import { TransitionModal } from '../../../organisms/TransitionModal/TransitionModal';
import { VideoConsultationProvidersPageData } from './constants';

let TRANSIT_TIMEOUT;

const ProvidersPage = (): React.ReactElement => {
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const { insuranceType, setInsuranceType, loadAvailableTimeslots, availableTimeslots } = useVideoConsultationStore();

  // grab insurance type from the url and compare with the store
  useInsuranceTypeParam(insuranceType, setInsuranceType, InsuranceType.PRIVATE);

  const { triggerTrackingEvent, triggerDatalayerEvent } = useContext(AppConfigContext);

  // load the timeslots after the first render
  useEffect(() => {
    if (!availableTimeslots) loadAvailableTimeslots(new Date());
  }, []);

  useEffect(() => {
    if (window.location.hash.indexOf('transit') == -1) {
      setShowTransitionModal(false);
      clearTimeout(TRANSIT_TIMEOUT);
    }
  }, [window.location.hash]);

  const heading = VideoConsultationProvidersPageData.headingsByType[insuranceType || InsuranceType.PRIVATE];
  const faqs = VideoConsultationProvidersPageData.faqsByType[insuranceType || InsuranceType.PRIVATE];
  const benefits = VideoConsultationProvidersPageData.benefitsByType[insuranceType || InsuranceType.PRIVATE];

  const timeslotsElement = (() => {
    if (insuranceType === InsuranceType.PRIVATE) {
      if (availableTimeslots) return <TimeslotsList timeslots={availableTimeslots} />;
      // TODO @Bencun: replace null with a spinner component or something similar in the future
      else return null;
    }
    return null;
  })();

  const transitionHandle = e => {
    e.preventDefault();
    setShowTransitionModal(true);
    window.location.href = ensureHash('#transit');
    TRANSIT_TIMEOUT = setTimeout(() => {
      triggerDatalayerEvent?.('zavaTransitionLoaded');
      triggerTrackingEvent?.('ods-client-transit-to-provider');
      location.href = `https://${ZAVA_VIDEO_CONSULTATION_REDIRECT_URI}`;
    }, 4 * 1000);

    triggerTrackingEvent?.('ods-client-video-consultation-book-now-click');
  };

  const child: React.ReactElement = (
    <>
      <Box pb={1.5}>
        <Typography variant="h6">{heading}</Typography>
      </Box>

      <Box pt={2} style={{ position: 'relative', height: '100%' }}>
        <ProviderTile provider={providerData} area={Area.TREATMENT_NOW} insuranceType={insuranceType}>
          <Feature featureName={Features.videoConsultationTimeSlotsEnabled}>
            <>{timeslotsElement}</>
          </Feature>
          {insuranceType === InsuranceType.PUBLIC ? (
            <>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                redirect={true}
                onClick={e => {
                  transitionHandle(e);
                }}
                id="ods_video_consultation_book_now"
              >
                Jetzt Videosprechstunde buchen
              </Button>
              <Box pt={2.5}>
                <Typography color={'textSecondary'} align="center">
                  Sie werden jetzt zu Zava weitergeleitet
                </Typography>
              </Box>
              <TransitionModal open={showTransitionModal} />
            </>
          ) : (
            <NotificationAppModal />
          )}
        </ProviderTile>
      </Box>
    </>
  );

  const child2: React.ReactElement = (
    <>
      <Box pb={3.7}>
        <BenefitBox elements={benefits} />
      </Box>
      <ListBox title="Benötigen Sie Hilfe?" subtitle="So funktioniert's" elements={faqs} />
    </>
  );

  return <OdsLayout children1={child} children2={child2} />;
};

export { ProvidersPage };
